'use client'

import { usePathname, useRouter } from 'next/navigation'
import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { PanelImperativeHandle } from 'react-resizable-panels'
import {
  CHAT_SIZE_PCT_KEY,
  readStoredString,
  writeStoredString,
} from '@/components/canvas/use-canvas-chats-utils'
import { confirmLeaveIfUnsaved } from '@/components/navigation/unsaved-changes-registry'
import { isEmailsGroupCanvasPath } from '@/lib/emails/emails-route'
import {
  type ChatPanelView,
  readChatViewCookie,
  readChatViewParamFromLocation,
  withChatListView,
  writeChatViewCookie,
} from './chat-panel-view-state'
import {
  type ChatSide,
  oppositeChatSide,
  readChatSideCookie,
  writeChatSideCookie,
} from './chat-side'
import {
  FULL_CHAT_LIST_PATH,
  fullChatPath,
  isExcludedChatPath,
  isFullChatListPath,
  parseFullChatId,
} from './excluded-paths'
import {
  createSplitHandoff,
  normalizeSplitHandoffHref,
  resolveSplitHandoff,
  type SplitHandoff,
} from './split-handoff'

type PendingSplitHandoff = {
  request: SplitHandoff
  sourceHref: string
  lastObservedHref: string
}

/** Default chat panel width (% of the row) in split mode. */
const DEFAULT_SPLIT_SIZE_PCT = 25
/** Clamp bounds for the persisted split width (mirrors the shell panel). */
const MIN_SPLIT_SIZE_PCT = 15
const MAX_SPLIT_SIZE_PCT = 80

/**
 * Read the persisted split width from localStorage, clamped to the valid
 * panel range. Falls back to {@link DEFAULT_SPLIT_SIZE_PCT} when unset or
 * malformed so a corrupted value can never strand the panel off-screen.
 */
function readStoredChatSizePct(): number {
  const raw = Number(
    readStoredString(CHAT_SIZE_PCT_KEY, String(DEFAULT_SPLIT_SIZE_PCT))
  )
  if (!Number.isFinite(raw)) {
    return DEFAULT_SPLIT_SIZE_PCT
  }
  return Math.min(Math.max(raw, MIN_SPLIT_SIZE_PCT), MAX_SPLIT_SIZE_PCT)
}

type UseChatSurfaceOptions = {
  /** Active chat of the shared tab strip (`useCanvasChats`). */
  activeChatId: string | null
  adoptActiveChat: (chatId: string) => void
  isLeftPanelOpen: boolean
  /** True after the composition owner restores its saved panel preference. */
  panelPreferenceReady: boolean
  setIsLeftPanelOpen: (open: boolean) => void
  /**
   * True while a user-initiated brand switch is in flight (`setBrandId` in
   * brand-context.tsx). The switch flips `brandId` to the new brand a beat
   * before the /home navigation lands, so the URL can still point at the
   * previous brand's `/chat/<id>` while the active brand is already the
   * new one; full-page chat adoption is suppressed in that window so the
   * previous brand's chat is never written into the new brand's tab strip.
   */
  isBrandSwitching: boolean
  initialChatSide?: ChatSide
  initialChatView?: ChatPanelView
}

/** The presentation slice of `GlobalChatContextValue` (see its docs). */
export type ChatSurfaceState = {
  fullChatId: string | null
  isFullPage: boolean
  isCollapsed: boolean
  setIsCollapsed: (collapsed: boolean) => void
  panelRef: RefObject<PanelImperativeHandle | null>
  toggleCollapsed: () => void
  ensurePanelExpanded: () => void
  chatSizePct: number
  setChatSizePct: (pct: number) => void
  chatSide: ChatSide
  toggleChatSide: () => void
  chatPanelView: ChatPanelView
  setChatPanelView: (view: ChatPanelView) => void
  expandToFull: () => void
  openChatFullPage: (chatId: string) => void
  collapseFromFull: () => void
  openPathInSplit: (path: string) => void
  replacePathPreservingSplit: (path: string) => void
  /** Watch committed URL changes only while an explicit artifact hop is pending. */
  hasPendingSplitHandoff: boolean
  observeSplitHandoff: (href: string) => void
  /** Admit the requested split page, retaining an intermediate email resolver. */
  consumeSplitHandoff: (pathname: string) => boolean
  /** Capture the current page before a full-chat Link or imperative navigation. */
  prepareFullChatNavigation: () => void
  /**
   * The last committed pathname, for event handlers and post-commit effects.
   * Lets consumers act on the route without subscribing to it.
   */
  getCommittedPathname: () => string
}

/**
 * Where and how the chat column is shown: full-page (`/chat/<id>`,
 * `/chats`) vs the split side panel, the panel's persisted view (thread |
 * list), side, width and collapse state, and the navigations between
 * those presentations. Owned by `GlobalChatProvider`; kept apart from the
 * conversation state (tabs, attachments, drafts) it sits beside.
 */
export function useChatSurface({
  activeChatId,
  adoptActiveChat,
  isLeftPanelOpen,
  panelPreferenceReady,
  setIsLeftPanelOpen,
  isBrandSwitching,
  initialChatSide,
  initialChatView,
}: UseChatSurfaceOptions): ChatSurfaceState {
  const pathname = usePathname()
  const router = useRouter()

  // Full-page chat mode: `/chat/<id>` renders the thread at 100% width and
  // `/chats` renders the chat-list overview at 100% width.
  const fullChatId = parseFullChatId(pathname)
  const isFullList = isFullChatListPath(pathname)
  const isFullPage = fullChatId !== null || isFullList

  // True only in SPLIT mode — the one presentation where the resizable
  // panel both renders content and belongs at the user's saved width.
  // `/home` parks it at 0% with no content; `/chat/<id>` and `/chats`
  // drive it to 100%. Imperative panel sizing must respect that.
  const isPanelRouteVisible = !isFullPage && !isExcludedChatPath(pathname ?? '')

  // The committed pathname, for the navigation callbacks below. They run
  // from event handlers and post-commit effects, so the ref is current when
  // read; closing over `pathname` instead re-created them, and with them the
  // provider's context value, on every navigation, re-rendering each
  // `useGlobalChat()` consumer (chat thread, message links and tool cards,
  // the canvas layout providers) inside the navigation's render.
  const pathnameRef = useRef(pathname)
  useLayoutEffect(() => {
    pathnameRef.current = pathname
  }, [pathname])
  const getCommittedPathname = useCallback(() => pathnameRef.current, [])

  // Persisted split-panel view (thread | list). Seeded from the SERVER-read
  // cookie so the first paint and hydration already agree on which header
  // and body the panel renders; the client cookie read is the fallback for
  // mounts without a server seed (tests, any future non-shell mount).
  const [chatPanelView, setChatPanelViewRaw] = useState<ChatPanelView>(
    () => initialChatView ?? readChatViewCookie()
  )
  const setChatPanelView = useCallback((view: ChatPanelView) => {
    writeChatViewCookie(view)
    setChatPanelViewRaw(view)
  }, [])

  // Adopt the path-encoded chat as active (no `?chat=` mirror) so the SAME
  // conversation persists when the user later navigates to a page (split mode
  // reads `activeChatId`). The full surfaces adopt the panel VIEW the same
  // way: leaving `/chats` for a page keeps the list in the side panel,
  // leaving `/chat/<id>` keeps that thread.
  //
  // The chat adopt is skipped while a brand switch is in flight (see
  // `isBrandSwitching`). The durable cross-brand-bleed fix is the
  // brand-ownership prune in `useCanvasChats` (it drops any tab whose chat
  // belongs to another brand, so a stale adopt self-heals); this guard just
  // avoids the transient write.
  useEffect(() => {
    if (isFullList) {
      setChatPanelView('list')
    } else if (fullChatId) {
      setChatPanelView('thread')
    }
    if (isBrandSwitching) {
      return
    }
    if (fullChatId && fullChatId !== activeChatId) {
      adoptActiveChat(fullChatId)
    }
  }, [
    fullChatId,
    isFullList,
    activeChatId,
    adoptActiveChat,
    isBrandSwitching,
    setChatPanelView,
  ])

  // Remember the last non-chat, non-excluded page so "collapse from full"
  // has somewhere sensible to land. Both full surfaces (`/chat/<id>` and
  // `/chats`) are skipped: collapsing to the side must never land on a page
  // whose only content IS the chat column.
  const lastPageRef = useRef<string>('/emails')
  const pendingSplitHandoffRef = useRef<PendingSplitHandoff | null>(null)
  const [hasPendingSplitHandoff, setHasPendingSplitHandoff] = useState(false)
  const clearSplitHandoff = useCallback(() => {
    pendingSplitHandoffRef.current = null
    setHasPendingSplitHandoff(false)
  }, [])
  const observeSplitHandoff = useCallback(
    (href: string) => {
      const pending = pendingSplitHandoffRef.current
      const destinationHref = normalizeSplitHandoffHref(href)
      if (!pending || destinationHref === pending.lastObservedHref) {
        return
      }
      pending.lastObservedHref = destinationHref
      // Observation retires mismatches. Split sizing owns admission so this
      // earlier layout effect cannot spend a final arrival before its panel.
      if (
        resolveSplitHandoff(pending.request, destinationHref) === 'cancelled'
      ) {
        clearSplitHandoff()
      }
    },
    [clearSplitHandoff]
  )
  const consumeSplitHandoff = useCallback(
    (destination: string) => {
      const pending = pendingSplitHandoffRef.current
      if (!pending) {
        return false
      }
      const destinationHref = normalizeSplitHandoffHref(
        `${destination}${window.location.search}`
      )
      // Expansion can commit before router.push. That unchanged source is
      // not an arrival; a return after departure is observed as a detour.
      if (
        destinationHref === pending.sourceHref &&
        pending.lastObservedHref === pending.sourceHref
      ) {
        return false
      }
      const result = resolveSplitHandoff(pending.request, destinationHref)
      if (result !== 'intermediate') {
        clearSplitHandoff()
      }
      return result !== 'cancelled'
    },
    [clearSplitHandoff]
  )
  const rememberCurrentPage = useCallback(() => {
    const currentPathname = pathnameRef.current
    if (isPanelRouteVisible && currentPathname) {
      lastPageRef.current = `${currentPathname}${window.location.search}${window.location.hash}`
    }
  }, [isPanelRouteVisible])
  useEffect(() => {
    if (!pathname) {
      return
    }
    if (parseFullChatId(pathname) || isFullChatListPath(pathname)) {
      return
    }
    if (isExcludedChatPath(pathname)) {
      return
    }
    lastPageRef.current = `${pathname}${window.location.search}${window.location.hash}`
  }, [pathname])

  // Resizable-panel UI slice. The panel ref + collapse state live here
  // so the global header toggle (collapsed → chat icon) and the shell's
  // resizable panel stay in sync. The stored open preference arrives after
  // hydration; the shell applies its restored collapse state imperatively.
  const panelRef = useRef<PanelImperativeHandle>(null)
  const [isCollapsed, setIsCollapsed] = useState(!isLeftPanelOpen)
  const [hasRestoredPanelPreference, setHasRestoredPanelPreference] =
    useState(panelPreferenceReady)
  if (panelPreferenceReady && !hasRestoredPanelPreference) {
    setHasRestoredPanelPreference(true)
    // Adopt a saved closure once after hydration. An open desktop preference
    // must not undo the shell's mobile page-entry collapse; later user actions
    // and full-chat returns continue to own presentation independently.
    if (!isLeftPanelOpen) {
      setIsCollapsed(true)
    }
  }

  // Seed from localStorage (lazy) so the panel mounts at the user's last
  // resized width — no jump from a hard-coded default on reload / remount.
  const [chatSizePct, setChatSizePctRaw] = useState(readStoredChatSizePct)
  const chatSizePctRef = useRef(chatSizePct)

  const setChatSizePct = useCallback((pct: number) => {
    const clamped = Math.min(
      Math.max(pct, MIN_SPLIT_SIZE_PCT),
      MAX_SPLIT_SIZE_PCT
    )
    chatSizePctRef.current = clamped
    setChatSizePctRaw(clamped)
    writeStoredString(CHAT_SIZE_PCT_KEY, String(clamped))
  }, [])

  // Side preference. Seeded from the SERVER-read cookie (`initialChatSide`)
  // so the first paint is already on the user's edge and hydration matches;
  // the client cookie read is only the fallback for mounts without a server
  // seed (tests, any future non-shell mount).
  const [chatSide, setChatSide] = useState<ChatSide>(
    () => initialChatSide ?? readChatSideCookie()
  )

  const toggleChatSide = useCallback(() => {
    setChatSide((current) => {
      const next = oppositeChatSide(current)
      writeChatSideCookie(next)
      return next
    })
  }, [])

  const openChatFullPage = useCallback(
    (chatId: string) => {
      rememberCurrentPage()
      router.push(fullChatPath(chatId))
    },
    [router, rememberCurrentPage]
  )

  const expandToFull = useCallback(() => {
    // Dragging the side panel to full width keeps what it was showing: the
    // list expands to `/chats`, a thread to `/chat/<id>`. The URL override
    // is read at call time (a resize gesture) so the provider never
    // subscribes to search params.
    if (
      chatPanelView === 'list' ||
      readChatViewParamFromLocation() === 'list'
    ) {
      rememberCurrentPage()
      router.push(FULL_CHAT_LIST_PATH)
      return
    }
    const target = activeChatId ?? fullChatId
    if (!target) {
      return
    }
    openChatFullPage(target)
  }, [
    chatPanelView,
    activeChatId,
    fullChatId,
    openChatFullPage,
    router,
    rememberCurrentPage,
  ])

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((collapsed) => {
      const shouldCollapse = !collapsed
      if (shouldCollapse) {
        panelRef.current?.collapse()
        setIsLeftPanelOpen(false)
      } else {
        panelRef.current?.expand()
        setIsLeftPanelOpen(true)
      }
      return shouldCollapse
    })
  }, [setIsLeftPanelOpen])

  const collapseFromFull = useCallback(() => {
    const lastPage = lastPageRef.current || '/emails'
    // Collapsing the full-width LIST keeps the user on the list: the side
    // panel opens on the overview (already adopted into the persisted view;
    // the param mirrors it onto the URL) instead of snapping back to a thread.
    router.push(isFullList ? withChatListView(lastPage) : lastPage)
  }, [router, isFullList])

  const ensurePanelExpanded = useCallback(() => {
    // Expand WITHOUT resetting the user's resized width — only force a
    // width when the panel is currently collapsed (there is no ratio to
    // preserve), restoring the last persisted width. When the panel group
    // isn't mounted (full-page chat / excluded routes) the resize is a
    // no-op and the split panel mounts at `defaultSize=chatSizePct`.
    //
    // The collapse test reads the PANEL, not `isCollapsed`: that state
    // tracks split-mode resizes only (the shell parks the panel to 0% on
    // excluded routes and to 100% on `/chat/<id>` without routing either
    // through `setIsCollapsed`), so it can report "open" for a panel that
    // is really shut. Gating on the stale value skipped the resize AND
    // made the `setIsCollapsed(false)` below a no-op — no re-render, so
    // the shell's layout effect never re-ran either and the panel stayed
    // closed. That was the templates page opening a chat nobody could see.
    //
    // …but ONLY on a route that actually shows the panel. On `/home` the
    // shell parks it at 0% and renders no panel content, so an imperative
    // expand there opens an EMPTY gap that survives until the next
    // navigation flips `mode` (the shell's layout effect keys off `mode` /
    // `isCollapsed`, and `setIsCollapsed(false)` is a no-op when it is
    // already false, so nothing re-renders to undo it). `openPathInSplit`
    // expands-then-navigates from exactly there. Recording the preference
    // is enough: arriving on a split route re-runs that layout effect.
    const panel = isPanelRouteVisible ? panelRef.current : null
    if (panel?.isCollapsed()) {
      panel.resize(`${chatSizePctRef.current}%`)
      // `resize` is clamped against the panel constraints; if the group
      // hasn't measured yet it can land back on `collapsedSize`.
      // `expand()` is the library's own restore path and is idempotent
      // once the panel is open.
      if (panel.isCollapsed()) {
        panel.expand()
      }
    }
    setIsCollapsed(false)
    setIsLeftPanelOpen(true)
  }, [isPanelRouteVisible, setIsLeftPanelOpen])

  const openPathInSplit = useCallback(
    (path: string) => {
      // A chat navigation pill has no link for the unsaved-changes guard to
      // intercept, so it asks the guard first (a dirty editor prompts). Then
      // open the chat beside the page, preserving the user's resized ratio
      // (persisted in `chatSizePct`) across the navigation so there is no
      // layout shift.
      void confirmLeaveIfUnsaved().then((shouldLeave) => {
        if (!shouldLeave) {
          return
        }
        // Explicit page handoffs keep chat open on mobile, including a hop
        // from another split page. Same-page interactions expand immediately
        // without leaving an exemption for a later return to this route.
        const request = createSplitHandoff(path)
        const currentPathname = pathnameRef.current
        const sourceHref = normalizeSplitHandoffHref(
          `${currentPathname ?? ''}${window.location.search}`
        )
        const shouldRetainHandoff =
          request.kind === 'email-resolver' ||
          !isPanelRouteVisible ||
          request.pathname !== currentPathname
        pendingSplitHandoffRef.current = shouldRetainHandoff
          ? { request, sourceHref, lastObservedHref: sourceHref }
          : null
        setHasPendingSplitHandoff(shouldRetainHandoff)
        ensurePanelExpanded()
        // Selection and camera focus are client state. The mounted group
        // already owns its board, so query-only focus must not rerun the
        // server page and its document seed reads.
        if (
          isEmailsGroupCanvasPath(currentPathname) &&
          request.kind === 'exact' &&
          request.pathname === currentPathname &&
          window.location.pathname === currentPathname
        ) {
          if (normalizeSplitHandoffHref(path) !== sourceHref) {
            window.history.pushState(null, '', path)
          }
          return
        }
        router.push(path)
      })
    },
    [router, ensurePanelExpanded, isPanelRouteVisible]
  )

  const replacePathPreservingSplit = useCallback(
    (path: string) => {
      if (panelRef.current?.isCollapsed() === false) {
        // The saved group may have already spent its arrival handoff. Admit
        // its canonical replacement once without reopening a closed panel.
        const sourceHref = normalizeSplitHandoffHref(
          `${pathnameRef.current ?? ''}${window.location.search}`
        )
        pendingSplitHandoffRef.current = {
          request: createSplitHandoff(path),
          sourceHref,
          lastObservedHref: sourceHref,
        }
        setHasPendingSplitHandoff(true)
      } else {
        clearSplitHandoff()
      }
      router.replace(path, { scroll: false })
    },
    [router, clearSplitHandoff]
  )

  // Stable identity: the provider spreads this into its memoized context
  // value, so a fresh object per render would re-render every consumer.
  return useMemo<ChatSurfaceState>(
    () => ({
      fullChatId,
      isFullPage,
      isCollapsed,
      setIsCollapsed,
      panelRef,
      toggleCollapsed,
      ensurePanelExpanded,
      chatSizePct,
      setChatSizePct,
      chatSide,
      toggleChatSide,
      chatPanelView,
      setChatPanelView,
      expandToFull,
      openChatFullPage,
      collapseFromFull,
      openPathInSplit,
      replacePathPreservingSplit,
      hasPendingSplitHandoff,
      observeSplitHandoff,
      consumeSplitHandoff,
      prepareFullChatNavigation: rememberCurrentPage,
      getCommittedPathname,
    }),
    [
      fullChatId,
      isFullPage,
      isCollapsed,
      toggleCollapsed,
      ensurePanelExpanded,
      chatSizePct,
      setChatSizePct,
      chatSide,
      toggleChatSide,
      chatPanelView,
      setChatPanelView,
      expandToFull,
      openChatFullPage,
      collapseFromFull,
      openPathInSplit,
      replacePathPreservingSplit,
      hasPendingSplitHandoff,
      observeSplitHandoff,
      consumeSplitHandoff,
      rememberCurrentPage,
      getCommittedPathname,
    ]
  )
}
